import type { Claim } from '../../../../models/claim'

/** Sentinel for the "…not on this list" dropdown option. */
export const NOT_LISTED = 'NOT_LISTED'

/** Step 2 (Truck & Driver) form state — the single source of truth (react-hook-form). */
export interface Step2FormValues {
  // Truck
  truckSelection: string // policy truck id | NOT_LISTED | ''
  truckPlate: string
  truckVin: string
  truckYear: string
  truckMake: string
  truckModel: string
  // Trailer
  trailerAttached: boolean | null
  trailerSelection: string // policy trailer id | NOT_LISTED | ''
  trailerPlate: string
  trailerVin: string
  trailerYearMakeModel: string
  trailerOwnership: string
  // Driver
  driverSelection: string // policy driver id | NOT_LISTED | ''
  driverName: string
  driverCdl: string
  relationship: string
  wasOnDuty: boolean | null
}

export const STEP2_DEFAULTS: Step2FormValues = {
  truckSelection: '',
  truckPlate: '',
  truckVin: '',
  truckYear: '',
  truckMake: '',
  truckModel: '',
  trailerAttached: null,
  trailerSelection: '',
  trailerPlate: '',
  trailerVin: '',
  trailerYearMakeModel: '',
  trailerOwnership: '',
  driverSelection: '',
  driverName: '',
  driverCdl: '',
  relationship: '',
  wasOnDuty: null,
}

/** Build Step 2 defaults from an existing claim (edit mode). A stored truck/trailer/
 * driver with a FK id selects that policy row; otherwise it's the "not on list" path. */
export function claimToStep2Defaults(claim: Claim): Step2FormValues {
  const t = claim.truck
  const tr = claim.trailer
  const d = claim.driver
  return {
    truckSelection: t ? (t.truckId ?? NOT_LISTED) : '',
    truckPlate: t?.licensePlate ?? '',
    truckVin: t?.vin ?? '',
    truckYear: t?.year != null ? String(t.year) : '',
    truckMake: t?.make ?? '',
    truckModel: t?.model ?? '',
    trailerAttached: claim.trailerAttached ?? null,
    trailerSelection: tr ? (tr.trailerId ?? NOT_LISTED) : '',
    trailerPlate: tr?.licensePlate ?? '',
    trailerVin: tr?.vin ?? '',
    trailerYearMakeModel: tr?.model ?? '',
    trailerOwnership: tr?.trailerOwnership ?? '',
    driverSelection: d ? (d.driverId ?? NOT_LISTED) : '',
    driverName: d ? `${d.firstName} ${d.lastName}`.trim() : '',
    driverCdl: d?.cdlNumber ?? '',
    relationship: d?.relationshipToInsured ?? '',
    wasOnDuty: d?.wasOnDuty ?? null,
  }
}

/** Fields that describe one unit or person, so an upload read fills them together or not at all. */
export const STEP2_GROUPS: (keyof Step2FormValues)[][] = [
  ['truckSelection', 'truckPlate', 'truckVin', 'truckYear', 'truckMake', 'truckModel'],
  [
    'trailerAttached',
    'trailerSelection',
    'trailerPlate',
    'trailerVin',
    'trailerYearMakeModel',
    'trailerOwnership',
  ],
  ['driverSelection', 'driverName', 'driverCdl'],
]
